import {
  IFlowOptions, PayCycleOptions, IPlan, Bank, ITokenData, CRMBearerTokenData, IExtra, ICity,
} from 'types';
import {
  CRMSimpleItem, CRMBank, CRMOptionSet, CRMCity, CRMProcessingPreference, CRMProductsWithDetails, CRMRegion, CRMOptionSetValue,
} from './crmTypes';
import {
  OPTION_OTHER, BANK_OTHER_ID, TO_DEACTIVATE_STRING, PLAN_IDS,
} from '../app/constants';

interface ConfigFromCRM {
  bankOptions: CRMBank[];
  leadSource: CRMSimpleItem[];
  industryTypes: CRMSimpleItem[];
  manageHrOptions: CRMOptionSet;
  managePayCycleOptions: CRMOptionSet<CRMProcessingPreference>;
  jobTitles: CRMOptionSet;
  payrollProcessingModes: CRMOptionSet;
  irdPaymentFrequencies: CRMOptionSet;
  employeeLeaveRollover: CRMOptionSet;
  accountantHasAccessTo: CRMOptionSet;

  payCycles: CRMOptionSet;

  cities: CRMCity[];
  regions: CRMRegion[];

  productsWithDetails: CRMProductsWithDetails[];

  competingProducts: CRMSimpleItem[];

  leadChannels: CRMSimpleItem[];
}

function flattenCRMOptionList(optionSet: CRMOptionSet): CRMOptionSetValue[] {
  return optionSet.optionSetValues;
}

function flattenCitiesCRMList(items: CRMCity[]): ICity[] {
  return items.map(({ id, name }) => ({ id, name }));
}

function flattenRegionsCRMList(items: CRMRegion[]): ICity[] {
  return items.map(({ id, name }) => ({ id, name }));
}

function normaliseBankOptionsFromCRM(bankOptions: CRMBank[]): Bank[] {
  return bankOptions.map(({
    name, id, processingFeeApplies, productVersions,
  }) => {
    const isFullService: boolean = productVersions?.some(({ webFormId }) => (webFormId === PLAN_IDS.CALCULATE_AND_MAKE_PAYMENTS_AUTOMATICALLY));
    const isCalculatorOnly: boolean = productVersions?.some(({ webFormId }) => (webFormId === PLAN_IDS.CALCULATE_ONLY));

    return {
      name,
      smartPayroll: isFullService,
      id,
      isCalculatorOnly,
      isFullService,
      level2: false,
      processingFeeApplies,
      isBankOther: (name === OPTION_OTHER || id === BANK_OTHER_ID),
    };
  });
}

function normalisePayCycleOptionsFromCRM(payCycleOptions: CRMOptionSet<CRMProcessingPreference>): PayCycleOptions[] {
  return payCycleOptions.optionSetValues.map(({
    name, description, webFormId, value,
  }) => ({
    id: webFormId,
    title: name,
    label: description,
    crmId: value,
  }));
}

function normalisePlansFromCRM(product: CRMProductsWithDetails | undefined): IPlan[] {
  console.log('normalisePlansFromCRM', product);

  if (!product || !product.plans) {
    return [];
  }

  const cleanPlans = product.plans.filter(({ webFormId }) => !!webFormId);

  return cleanPlans.map(({
    text, features, webFormId, title, unavailableText, id,
  }) => ({
    id: webFormId,
    smartPayroll: webFormId === 'calculate-and-automate',
    title,
    text,
    features,
    unavailableText,
    crmId: id,
  }));
}

function normaliseExtrasFromCRM(product: CRMProductsWithDetails | undefined): IExtra[] {
  console.log('normaliseExtrasFromCRM', product);

  if (!product) {
    return [];
  }

  const {
    name,
    text,
    features = [],
  } = product;

  return [{
    id: 'smart-hr',
    title: name,
    text,
    features,
  }];
}

function normaliseProductsWithDetails(productsWithDetails: CRMProductsWithDetails[]): { plans: IPlan[]; extras: IExtra[] } {
  const productWithPlans = productsWithDetails.find((productWithDetail) => productWithDetail.id === '3b1c250f-0982-e211-bbfd-005056ae0102');
  const productWithExtras = productsWithDetails.find((productWithDetail) => productWithDetail.id === '566c0632-fed4-e511-8fb1-005056ae0102');

  const plans = normalisePlansFromCRM(productWithPlans);
  const extras = normaliseExtrasFromCRM(productWithExtras);


  return {
    plans,
    extras,
  };
}

export function normaliseConfigFromCRM(props: ConfigFromCRM): IFlowOptions {
  const {
    bankOptions,
    productsWithDetails,
    leadSource,
    industryTypes,
    manageHrOptions,
    managePayCycleOptions,
    jobTitles,
    payrollProcessingModes,
    irdPaymentFrequencies,
    employeeLeaveRollover,
    accountantHasAccessTo,
    payCycles,
    cities,
    regions,
    competingProducts,
    leadChannels,
  } = props;

  const {
    plans,
    extras,
  } = normaliseProductsWithDetails(productsWithDetails);

  return {
    bankOptions: normaliseBankOptionsFromCRM(bankOptions),
    leadSource: leadSource.filter(({ name }) => !name.includes(TO_DEACTIVATE_STRING)),
    industryTypes: industryTypes.filter(({ name }) => !name.includes(TO_DEACTIVATE_STRING)),
    manageHrOptions: flattenCRMOptionList(manageHrOptions),
    managePayCycleOptions: normalisePayCycleOptionsFromCRM(managePayCycleOptions),
    plans,
    jobTitles: flattenCRMOptionList(jobTitles),
    payrollProcessingModes: flattenCRMOptionList(payrollProcessingModes),
    irdPaymentFrequencies: flattenCRMOptionList(irdPaymentFrequencies),
    employeeLeaveRollover: flattenCRMOptionList(employeeLeaveRollover),
    accountantHasAccessTo: flattenCRMOptionList(accountantHasAccessTo),
    extras,
    payCycles: flattenCRMOptionList(payCycles),
    cities: flattenCitiesCRMList(cities),
    regions: flattenRegionsCRMList(regions),
    competingProducts,
    leadChannels,
  };
}

export function normaliseToken(data: CRMBearerTokenData): ITokenData {
  return {
    accessToken: data.access_token,
    expiresIn: data.expires_in,
    expiresOn: data.expires_on,
    extExpiresIn: data.ext_expires_in,
    notBefore: data.not_before,
    resource: data.resource,
    tokenType: data.token_type,
  };
}
